import { useEffect, useState } from 'react';
import { Link, useStaticQuery, graphql } from 'gatsby';
import { AnimatePresence, motion } from 'framer-motion';
import { Logo } from '../logo';
import Portal from './portal';
import MenuIcon from './menu-icon';
import { Facebook, Twitter, Youtube, Email, Phone } from '../footer/icons';
import { links } from '../footer';

const menu = [
  { url: '/', title: 'Home' },
  { url: null, title: 'Stories' },
  { url: '/covid-response', title: 'Covid Response' },
  { url: '/program-highlights', title: 'Program Highlights' },
  { url: '/funding-partners', title: 'Funding Partners' },
  { url: '/trustees-staff', title: 'Trustees / Staff' },
];

export const Header = ({ path }) => {
  const data = useStaticQuery(graphql`
    query HeaderQuery {
      stories: allFile(
        filter: {
          sourceInstanceName: { eq: "stories" }
          extension: { eq: "md" }
        }
        sort: {
          fields: childrenMarkdownRemark___frontmatter___order
          order: ASC
        }
      ) {
        nodes {
          childMarkdownRemark {
            id
            fields {
              slug
            }
            frontmatter {
              order
              project
              title
            }
          }
        }
      }
    }
  `);

  const stories = data.stories.nodes.map((file) => file.childMarkdownRemark);

  const [dropdownIsOpen, setDropdownOpen] = useState();

  const handleMouseEnter = () => {
    setDropdownOpen(true);
  };
  const handleMouseLeave = () => {
    setDropdownOpen(false);
  };

  const [isOpen, setIsOpen] = useState(false);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        setIsOpen(false);
      }, 1000);
    }
  }, [path]);

  return (
    <header className="header">
      <nav className="header__nav">
        <a href="https://northlandfdn.org" target="_blank" className="header__logo">
          <Logo width="185px" />
        </a>
        <button
          className={['header__mobile-toggle', isOpen && 'is-open']
            .filter(Boolean)
            .join(' ')}
          onClick={toggleMenu}
        >
          <MenuIcon isOpen={false} />
        </button>
        <Portal id="mobile-menu" className="mobile-portal">
          <AnimatePresence>
            {isOpen && (
              <motion.div
                className="header__mobile-wrapper"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  duration: 0.4,
                  type: 'tween',
                }}
              >
                <Link to="/" className="header__logo">
                  <Logo width="185px" />
                </Link>
                <button
                  className={['header__mobile-toggle', isOpen && 'is-open']
                    .filter(Boolean)
                    .join(' ')}
                  onClick={toggleMenu}
                >
                  <MenuIcon isOpen={true} />
                </button>
                <motion.ul
                  className="header__mobile-menu"
                  initial={{ y: 25 }}
                  animate={{ y: 0 }}
                  exit={{ y: 25 }}
                  transition={{
                    duration: 0.2,
                    type: 'tween',
                  }}
                >
                  {menu.map(({ url, title }) => {
                    if (title === 'Stories') {
                      return (
                        <li
                          key={url}
                          className={[
                            'header__menu-item',
                            dropdownIsOpen && 'has-open-dropdown',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                        >
                          <span className="header__menu-item__has-children">
                            {title}:
                            <ul className="">
                              {stories.map((story) => {
                                const url = `/stories${story.fields.slug}`;
                                const title = story.frontmatter.project;
                                return (
                                  <li key={url} className="header__menu-item">
                                    {url && <Link to={url}>{title}</Link>}
                                  </li>
                                );
                              })}
                            </ul>
                          </span>
                        </li>
                      );
                    }

                    return (
                      <li key={url} className="header__menu-item">
                        {url && <Link to={url}>{title}</Link>}
                      </li>
                    );
                  })}
                </motion.ul>
                <nav className="footer__nav">
                  <div className="footer__inner">
                    <span>
                      © {new Date().getFullYear()}{' '}
                      <a href="https://northlandfdn.org">
                        Northland Foundation
                      </a>
                    </span>
                    <ul className="footer__menu">
                      {links.map(({ url, Icon, newTab, noBackground }) => (
                        <li key={url} className="footer__menu-item">
                          <a
                            className={noBackground && 'no-background'}
                            href={url}
                            {...(newTab && {
                              target: '_blank',
                              rel: 'noopener noreferrer',
                            })}
                          >
                            <Icon />
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                </nav>
              </motion.div>
            )}
          </AnimatePresence>
        </Portal>

        <ul className="header__menu">
          {menu.map(({ url, title }) => {
            if (title === 'Stories') {
              return (
                <li
                  key={url}
                  className={[
                    'header__menu-item',
                    dropdownIsOpen && 'has-open-dropdown',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  <span
                    className="header__menu-item__dropdown"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                  >
                    {title}
                    <Portal id="stories-menu" className="header-portal">
                      <div
                        className={[
                          'header-portal__container',

                          dropdownIsOpen && 'is-open',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <ul className="header-portal__menu">
                          {stories.map((story) => {
                            return (
                              <StoryMenuItem
                                key={story.fields.slug}
                                story={story}
                              />
                            );
                          })}
                        </ul>
                      </div>
                    </Portal>
                  </span>
                </li>
              );
            }

            return (
              <li key={url} className="header__menu-item">
                {url && <Link to={url}>{title}</Link>}
              </li>
            );
          })}
        </ul>
      </nav>
    </header>
  );
};

const StoryMenuItem = ({ story }) => {
  return (
    <li className="story-menu-item">
      <Link to={`/stories${story.fields.slug}`}>
        <span className="flag">
          {story.frontmatter.project}
          {/* <Vine flip={false} /> */}
        </span>
        <span className="h3">{story.frontmatter.title}</span>
      </Link>
    </li>
  );
};
